import { formatHours, formatPercent, formatSigned } from '@algo/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ConsistencyCalendar,
  DifficultyChart,
  RatingChart,
  SolvedOverTimeChart,
  TopicMasteryRadar,
} from '../components/charts';
import { Badge, Card, EmptyState, ErrorState, PageHeader, ProgressBar, Spinner } from '../components/ui';
import { api, qk } from '../lib/api';

function StatTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'success' | 'danger' | 'muted';
}) {
  const toneClass =
    tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-muted';
  return (
    <div className="card p-4">
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1.5 font-mono text-2xl font-semibold tabular-nums">{value}</p>
      {sub && <p className={`mt-1 text-xs ${toneClass}`}>{sub}</p>}
    </div>
  );
}

export function DashboardPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: qk.dashboard,
    queryFn: () => api.getDashboard(),
  });

  if (isLoading) return <Spinner label="Сургалтын мэдээллийг ачаалж байна…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const weekly = data.weeklyProgress;
  const weeklyTone = (weekly.percentChange ?? 0) >= 0 ? 'success' : 'danger';
  const goalRatio =
    data.today.tasks.length === 0
      ? 0
      : data.today.tasks.filter((task) => task.done).length / data.today.tasks.length;

  return (
    <>
      <PageHeader
        title="Хяналтын самбар"
        description={`${data.tier} · ${data.rating} оноо${
          data.nextTier ? ` · ${data.nextTier.tier} хүртэл ${data.nextTier.pointsAway} оноо` : ''
        }`}
      />

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
        <StatTile
          label="Рэйтинг"
          value={String(data.rating)}
          sub={`${data.breakdown.problemPoints} бодлого · ${data.breakdown.consistencyBonus} тогтвортой байдал · ${data.breakdown.coverageBonus} хамрах хүрээ`}
        />
        <StatTile
          label="Бодсон бодлого"
          value={String(data.totalSolvedProblems)}
          sub={`${data.totalAttempts} амжилттай оролдлого`}
        />
        <StatTile
          label="Одоогийн цуваа"
          value={`${data.currentStreak} хоног`}
          sub={`Хамгийн урт ${data.longestStreak} хоног`}
        />
        <StatTile
          label="Долоо хоногийн ахиц"
          value={formatPercent(weekly.percentChange)}
          sub={`Энэ долоо хоногт ${formatSigned(weekly.currentWeekPoints)} оноо, өмнөх ${weekly.previousWeekPoints}`}
          tone={weeklyTone}
        />
        <StatTile
          label="Суралцсан хугацаа"
          value={formatHours(data.totalLearningHours * 60)}
          sub={`${data.breakdown.activeDayCount} идэвхтэй хоног`}
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Card
          title="Бодсон бодлогын явц"
          subtitle="ISO долоо хоног тутмын шинэ бодлого"
          className="xl:col-span-2"
        >
          <SolvedOverTimeChart data={data.solvedOverTime} />
        </Card>

        <Card title="Өнөөдөр" subtitle={data.today.day}>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted">Бодлого</span>
              <span className="font-mono">{data.today.problemsSolved}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted">Минут</span>
              <span className="font-mono">{data.today.minutes}</span>
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between text-sm">
                <span className="text-muted">Даалгавар</span>
                <span className="font-mono">
                  {data.today.tasks.filter((task) => task.done).length}/{data.today.tasks.length}
                </span>
              </div>
              <ProgressBar value={goalRatio} />
            </div>
            <ul className="space-y-1.5 pt-1">
              {data.today.tasks.slice(0, 5).map((task) => (
                <li key={task.id} className="flex items-start gap-2 text-sm">
                  <span className={task.done ? 'text-success' : 'text-muted'}>
                    {task.done ? '✓' : '○'}
                  </span>
                  <span className={task.done ? 'text-muted line-through' : ''}>{task.title}</span>
                </li>
              ))}
              {data.today.tasks.length === 0 && (
                <li className="text-sm text-muted">
                  Төлөвлөгөө алга —{' '}
                  <Link to="/daily" className="text-accent underline underline-offset-2">
                    өнөөдрийн даалгавраа тохируул
                  </Link>
                  .
                </li>
              )}
            </ul>
          </div>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Card title="Рэйтингийн явц" className="xl:col-span-2">
          {data.ratingHistory.length > 1 ? (
            <RatingChart data={data.ratingHistory} />
          ) : (
            <EmptyState
              title="Түүх хараахан хангалтгүй"
              description="Рэйтинг хоёроос доошгүй удаа өөрчлөгдсөний дараа график гарч ирнэ."
            />
          )}
        </Card>

        <Card title="Хүндрэлийн тархалт" subtitle="Бодсон ялгаатай бодлого">
          <DifficultyChart data={data.difficultyDistribution} />
        </Card>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Card title="Сэдвийн эзэмшилт" className="xl:col-span-2">
          <TopicMasteryRadar data={data.topicMastery} />
        </Card>

        <div className="flex flex-col gap-4">
          <Card title="Сул сэдвүүд" subtitle="Итгэл багатай эсвэл сүүлд бага дасгал хийсэн">
            {data.weakTopics.length === 0 ? (
              <EmptyState title="Сул сэдэв алга" description="Эхэлсэн бүх сэдэв сайн байна." />
            ) : (
              <ul className="space-y-2.5">
                {data.weakTopics.map((topic) => (
                  <li key={topic.topicId} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm">{topic.name}</p>
                      <p className="text-xs text-muted">{topic.reason}</p>
                    </div>
                    <Badge tone="warning">{topic.confidence}/5</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Хүчтэй сэдвүүд" subtitle="Итгэл ≥ 4 ба ≥ 10 бодлого">
            {data.strongTopics.length === 0 ? (
              <EmptyState
                title="Одоохондоо алга"
                description="Аль нэг сэдэвт итгэл 4, бодсон бодлого 10-д хүрэх шаардлагатай."
              />
            ) : (
              <ul className="space-y-2.5">
                {data.strongTopics.map((topic) => (
                  <li key={topic.topicId} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm">{topic.name}</p>
                      <p className="text-xs text-muted">{topic.problemsSolved} бодлого бодсон</p>
                    </div>
                    <Badge tone="success">{topic.confidence}/5</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Card
          title="Тогтвортой байдал"
          subtitle="≥ 1 бодлого бодсон эсвэл ≥ 30 минут бүртгэсэн өдөр тооцогдоно"
          className="xl:col-span-2"
        >
          <ConsistencyCalendar days={data.heatmap} />
        </Card>

        <Card title="Сүүлийн амжилтууд">
          {data.recentAchievements.length === 0 ? (
            <EmptyState title="Одоохондоо нээгээгүй" description="Эхлэхийн тулд анхны бодолтоо бүртгэ." />
          ) : (
            <ul className="space-y-3">
              {data.recentAchievements.map((achievement) => (
                <li key={achievement.id} className="flex items-center gap-3">
                  <span className="text-xl" aria-hidden>
                    {achievement.icon}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{achievement.name}</p>
                    <p className="truncate text-xs text-muted">{achievement.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
